import * as React from 'react';
import {
    TextField,
    styled,
    Grid,
    Container as BaseContainer,
} from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import SectionGrid from '../section-grid';
import { brand } from '../../theme';

export type InputChangeEventHandler = (id: string, value: any) => void;

/** Adapts MUI's onChange event to the parent EventEditForm's `{ field: value }`
 *  shape. Used by every text field in this module so they all speak the same
 *  edit-protocol the form expects. */
export const wrapOnChange = (p: any, parseValue?: ((v: any) => any)) => {
    const pV = parseValue || (v => v);
    return (p.onChange ?
        //Fake an onChange as a change event.
        {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                p.onChange({ [event?.target?.id]: pV(event?.target?.value) })
            }
        }
        :
        {}
    )
};

export const parseToInt = (defaultValue: any) => (value: any) => {
    const parsed = parseInt(value);
    if (isNaN(parsed)) {
        return defaultValue;
    }
    return parsed;
};

export const Container = styled(p =>
    <BaseContainer {...p} noValidate="novalidate" component="form" maxWidth='md' />
)(({ theme }) => ({
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(10),
    background: brand.surface,
}));

export const Section = SectionGrid;

export const NameField = styled(p =>
    <Grid item>
        <TextField
            id="name"
            variant="outlined"
            margin="normal"
            required
            fullWidth
            inputProps={{ "data-testid": "name" }}
            label="Name"
            name="name"
            placeholder="My Special Event"
            autoComplete="off"
            {...p}
            {...wrapOnChange(p)}
        />
    </Grid>
)({});

export const VenueField = styled(p =>
    <Grid item>
        <TextField
            id="venue"
            variant="outlined"
            margin="normal"
            required
            fullWidth
            inputProps={{ "data-testid": "venue" }}
            label="Venue"
            name="venue"
            placeholder="Drink Drunk Bar"
            autoComplete="off"
            {...p}
            {...wrapOnChange(p)}
        />
    </Grid>
)({});

export const DateField = styled(p =>
    <Grid item>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDateTimePicker
                id="date"
                inputProps={{ "data-testid": "date" }}
                margin="normal"
                variant="inline"
                inputVariant="outlined"
                ampm={true}
                disablePast
                minDateMessage={'Date should be in the future'}
                fullWidth
                label={"Date & Time"}
                required
                mask={"__-__-____  __:__ _m"}
                format={"dd-MM-yyyy  hh:mm aaa"}
                {...p}
                {...(p.onChange ?
                    //Fake an onChange as a change event.
                    { onChange: (date) => p.onChange({ "date": date }) }
                    :
                    {}
                )}
            />
        </MuiPickersUtilsProvider>
    </Grid>
)({});

export const PriceField = styled(p =>
    <Grid item>
        <TextField
            id="price"
            variant="outlined"
            margin="normal"
            required
            fullWidth
            inputProps={{ "data-testid": "price" }}
            label="Price"
            name="price"
            placeholder="Alf. 75"
            autoComplete="off"
            {...p}
            {...wrapOnChange(p)}
        />
    </Grid>
)({});

export const BartenderEmailField = styled(p =>
    <Grid item>
        <TextField
            id="bartender"
            variant="outlined"
            margin="normal"
            required
            fullWidth
            inputProps={{ "data-testid": "bartender" }}
            label="Bartender's Email"
            name="bartender"
            placeholder="bartender@boncos.io"
            {...p}
            {...wrapOnChange(p)}
        />
    </Grid>
)({});

export const RoundsField = styled(p =>
    <Grid item>
        <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="rounds"
            label="Rounds"
            name="rounds"
            placeholder="10"
            autoComplete="off"
            {...p}
            {...wrapOnChange(p, parseToInt(10))}
        />
    </Grid>
)({});

export const AsterisksAllowedField = styled((p) => (
    <Grid item>
        <TextField
            id="asterisksAllowed"
            variant="outlined"
            margin="normal"
            fullWidth
            inputProps={{ "data-testid": "asterisksAllowed" }}
            label="Asterisks' Allowed"
            name="asterisksAllowed"
            placeholder="2"
            {...p}
            {...wrapOnChange(p, parseToInt(0))}
        />
    </Grid>
))({});
